/**
 * @file Login page.
 * @description Handles user sign-in with credential validation, loading state, and post-login redirect.
 * @author Sanjay Jangid (Jiggle frontend)
 * @email sanjay.jangid@dgtoohl.com
 * @date 2026-05-10
 * @version 0.0.0
 */
import React, { useState } from 'react';
import { Eye, EyeOff, Lock, Mail } from 'lucide-react';
import { AuthService } from '../services/authService';
import { Input, Button } from '../components/ui';
import { LoginForm } from '../types';
import { useNavigate } from 'react-router-dom';

// --- Main Login Component ---
const Login: React.FC = () => {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    if (!email || !password) {
      setError('Please enter email and password.');
      return;
    }

    setIsLoading(true);

    try {
      const credentials: LoginForm = { email, password };
      const result = await AuthService.login(credentials);

      if (result.status === 'success' && (result.data?.access_token || result.data?.token)) {
        setSuccess(result.message + ' Redirecting...');
        navigate('/dashboard', { replace: true });
      } else {
        throw new Error(result.message || 'Login failed.');
      }
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'An unknown error occurred.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="relative flex min-h-screen items-center justify-center overflow-hidden bg-gradient-to-br from-primary-500 to-secondary-500 p-4 font-sans">

      {/* Floating Animated Blobs */}
      <div className="absolute w-[30rem] h-[30rem] bg-pink-400/30 rounded-full blur-3xl top-[-10%] left-[-10%] animate-pulse"></div>
      <div className="absolute w-[25rem] h-[25rem] bg-purple-500/30 rounded-full blur-2xl bottom-[-10%] right-[-10%] animate-bounce"></div>

      {/* Card */}
      <div className="relative w-full max-w-md rounded-3xl border border-white/30 bg-white/20 p-4 shadow-sm backdrop-blur-xl transition-transform duration-300 hover:scale-[1.01] p-8 py-16">
        <h2 className="mb-2 text-center text-3xl font-extrabold text-white drop-shadow-lg sm:text-4xl"> Jiggle</h2>
        <p className="text-md mb-6 text-center text-white/90 sm:mb-8">Your campaign manager, simplified.</p>

        <form onSubmit={handleSubmit} className="mt-6 space-y-5 sm:mt-10 sm:space-y-6">
          {/* Email */}
          <div className="relative">
            <Mail
              className="pointer-events-none absolute left-3 top-1/2 z-10 -translate-y-1/2 text-white"
              size={20}
              aria-hidden
            />

            <Input
              type="email"
              placeholder="Email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              disabled={isLoading}
              autoComplete="email"
              className="login-input-autofill border border-white/80 bg-transparent py-3 pl-10 text-white shadow-none outline-none ring-0 placeholder:text-white/60 focus:border-white focus:shadow-none focus:outline-none focus:ring-0"
            />
          </div>

          {/* Password */}
          <div className="relative">
            <Lock
              className="pointer-events-none absolute left-3 top-1/2 z-10 -translate-y-1/2 text-white"
              size={20}
              aria-hidden
            />

            <Input
              type={showPassword ? 'text' : 'password'}
              placeholder="Password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              disabled={isLoading}
              autoComplete="current-password"
              className="login-input-autofill border border-white/80 bg-transparent py-3 pl-10 pr-11 text-white shadow-none outline-none ring-0 placeholder:text-white/60 focus:border-white focus:shadow-none focus:outline-none focus:ring-0"
            />

            <button
              type="button"
              onClick={() => setShowPassword((prev) => !prev)}
              disabled={isLoading}
              className="absolute right-3 top-1/2 z-10 -translate-y-1/2 text-white hover:text-white/90 focus:outline-none disabled:opacity-50"
              aria-label={showPassword ? 'Hide password' : 'Show password'}
            >
              {showPassword ? (
                <EyeOff size={20} aria-hidden />
              ) : (
                <Eye size={20} aria-hidden />
              )}
            </button>
          </div>

          {/* Messages */}
          {success && (
            <div className="text-green-200 bg-green-800/40 p-3 rounded-lg text-center text-sm font-medium animate-fade-in">
              {success}
            </div>
          )}
          {error && (
            <div className="text-red-300 bg-red-900/40 p-3 rounded-lg text-center text-sm font-medium animate-fade-in">
              {error}
            </div>
          )}

          {/* Submit */}
          <Button
            type="submit"
            variant="unstyled"
            size="lg"
            loading={isLoading}
            className="w-full bg-white font-semibold text-black shadow-md transition-all duration-300 ease-in-out hover:-translate-y-0.5 hover:bg-gradient-to-r hover:from-primary-100 hover:to-secondary-100 hover:text-black hover:shadow-lg active:translate-y-0"
          >
            {isLoading ? 'Logging in...' : 'Login'}
          </Button>
        </form>
      </div>
    </div>
  );
};

export default Login;
